export type ArticleCategory = "theory" | "review" | "culture";

export type ContentBlock =
  | { type: "h2"; text: string }
  | { type: "p"; text: string }
  | { type: "quote"; text: string }
  | { type: "image"; src: string; alt: string };

export interface Article {
  id: string;
  title: string;
  summary: string;
  author: string;
  date: string;
  category: ArticleCategory;
  tags: string[];
  cover: string;
  heroImage?: string;
  views: number;
  favorites: number;
  featured: boolean;
  content: ContentBlock[];
}

export interface CategoryMeta {
  id: ArticleCategory;
  name: string;
  enName: string;
  description: string;
  intro: string;
  icon: string;
  path: string;
}

export const categoryMetas: CategoryMeta[] = [
  {
    id: "theory",
    name: "影视理论",
    enName: "Film Theory",
    description: "蒙太奇、长镜头、符号学与凝视理论等电影理论的核心命题。",
    intro:
      "从爱森斯坦到巴赞，从电影符号学到女性主义批评，影视理论为我们提供理解影像语言的坐标系。本栏目结集成体系的理论探讨，兼顾学术脉络与当代回响。",
    icon: "ri-book-open-line",
    path: "/category/theory",
  },
  {
    id: "review",
    name: "电影评论",
    enName: "Film Review",
    description: "面向经典与当代佳作的深度影评与细读。",
    intro:
      "评论不止于好恶，更在于解释影像如何运作。本栏目以细读的方式进入具体影片，讨论其场面调度、叙事结构与情感逻辑，让观看变得更有据可循。",
    icon: "ri-film-line",
    path: "/category/review",
  },
  {
    id: "culture",
    name: "影视文化与研究",
    enName: "Film Culture & Research",
    description: "产业观察、文化研究与媒介变迁中的电影。",
    intro:
      "电影从来不只是电影。本栏目关注产业格局、电影节生态、流媒体转型与观看方式变迁，把影像放回它所处的文化与媒介语境中加以考察。",
    icon: "ri-earth-line",
    path: "/category/culture",
  },
];

export const articles: Article[] = [
  {
    id: "montage-revisited",
    title: "蒙太奇理论再审视：爱森斯坦的辩证法与当代影像",
    summary:
      "当两个镜头相遇，意义在碰撞中诞生。回到爱森斯坦的辩证蒙太奇，我们得以重新理解剪辑不只是连接，而是一种思想的生产方式。",
    author: "沈砚",
    date: "2026-09-28",
    category: "theory",
    tags: ["蒙太奇", "爱森斯坦", "剪辑理论"],
    cover:
      "https://readdy.ai/api/search-image?query=Cinematic%20editorial%20still%20of%20a%20vintage%20film%20projector%20beam%20cutting%20through%20dusty%20dark%20air%2C%20desaturated%20blue-gray%20and%20warm%20beige%20tones%2C%20film%20grain%2C%20soft%20shadows%2C%20elegant%20magazine%20aesthetic%2C%20atmospheric%20and%20shallow%20depth%20of%20field&width=800&height=500&seq=tf-theory-01&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20vintage%20film%20projector%20in%20a%20dark%20room%20casting%20a%20wide%20beam%20through%20floating%20dust%2C%20desaturated%20deep%20blue-gray%20with%20warm%20beige%20highlights%2C%20film%20grain%2C%20moody%20editorial%20cinematography%2C%20negative%20space%20for%20text%2C%20atmospheric%20and%20elegant&width=1920&height=1080&seq=tf-hero-01&orientation=landscape",
    views: 18420,
    favorites: 1260,
    featured: true,
    content: [
      {
        type: "p",
        text: "蒙太奇（Montage）远不止是一种剪辑技巧。在爱森斯坦手中，它是一套关于意义如何生成的哲学装置。两个独立的画面并置，观众的心智在二者之间完成一次跳跃，新的含义便在这一跃中诞生。",
      },
      { type: "h2", text: "冲突，而非连续" },
      {
        type: "p",
        text: "与后来好莱坞强调流畅连贯的剪辑观念不同，爱森斯坦主张镜头之间应当是「冲突」的。他把电影视为一种辩证的书写，正题与反题在剪辑点上碰撞，产生作为合题的新意义。",
      },
      {
        type: "quote",
        text: "蒙太奇不是把画面连接起来，而是让画面彼此碰撞出思想。",
      },
      { type: "h2", text: "在当代影像中的回响" },
      {
        type: "p",
        text: "今天的音乐录影带、短视频剪辑乃至算法推荐的信息流，都在某种意义上实践着一种被稀释的蒙太奇逻辑：并置即意义。重新阅读爱森斯坦，并非怀旧，而是为理解当下的影像环境提供一把钥匙。",
      },
      {
        type: "image",
        src: "https://readdy.ai/api/search-image?query=Abstract%20geometric%20composition%20of%20overlapping%20film%20frames%20and%20light%20shafts%2C%20desaturated%20blue-gray%20with%20warm%20beige%20accents%2C%20grainy%20analog%20texture%2C%20editorial%20minimal%20design%2C%20balanced%20negative%20space%2C%20calm%20and%20intellectual%20mood&width=1200&height=600&seq=tf-theory-01b&orientation=landscape",
        alt: "由叠加胶片画格与光束构成的抽象构图，呈现蓝灰与米黄色调",
      },
      {
        type: "p",
        text: "理解蒙太奇，最终是为了理解观看本身：我们从来不是被动地接收画面，而是在画面之间主动地制造意义。",
      },
    ],
  },
  {
    id: "long-take-bazin",
    title: "长镜头美学的哲学根基：巴赞与现实主义电影观",
    summary:
      "巴赞相信，电影的伦理在于对现实保持尊重。长镜头与景深调度因此不只是风格选择，而是一种让世界自行显现的态度。",
    author: "林叙",
    date: "2026-09-21",
    category: "theory",
    tags: ["长镜头", "巴赞", "现实主义"],
    cover:
      "https://readdy.ai/api/search-image?query=Long%20empty%20courtyard%20corridor%20seen%20through%20deep%20focus%20camera%20perspective%2C%20quiet%20realism%2C%20muted%20blue-gray%20and%20warm%20beige%20palette%2C%20soft%20natural%20light%2C%20film%20grain%2C%20editorial%20photography%2C%20contemplative%20atmosphere&width=800&height=500&seq=tf-theory-02&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20an%20empty%20realist%20courtyard%20in%20soft%20overcast%20light%2C%20muted%20blue-gray%20and%20pale%20beige%20tones%2C%20subtle%20film%20grain%2C%20editorial%20photography%2C%20calm%20contemplative%20atmosphere%2C%20generous%20negative%20space%20for%20headline%20text&width=1920&height=1080&seq=tf-hero-02&orientation=landscape",
    views: 15230,
    favorites: 980,
    featured: true,
    content: [
      {
        type: "p",
        text: "安德烈·巴赞把电影的本体论奠基于「现实的渐近线」之上。在他看来，影像的价值在于它保留了现实的多义与暧昧，而不是替观众做出解释。",
      },
      { type: "h2", text: "不做切割的尊重" },
      {
        type: "p",
        text: "长镜头与景深镜头之所以重要，是因为它们拒绝替观众安排视线。当一场戏在一个连续的时间里展开，观众获得的是选择的权利：看什么、看多久、如何理解，都交还给观看者。",
      },
      {
        type: "quote",
        text: "剪辑把世界切成碎片，而景深让我们看见世界的完整。",
      },
      { type: "h2", text: "一种观看的伦理" },
      {
        type: "p",
        text: "因此，巴赞的长镜头理论本质上是一种伦理主张：对现实保持耐心，对他者保持开放。这也是它至今仍能启发创作的原因。",
      },
      {
        type: "p",
        text: "今天，当影像越来越倾向于替我们完成所有情绪的时候，巴赞的提醒显得格外珍贵。",
      },
    ],
  },
  {
    id: "semiotics-metz",
    title: "电影符号学入门：麦茨与影像语言的结构",
    summary:
      "电影真的是一种语言吗？麦茨用符号学的方法追问这个问题，并给出了一个至今仍被讨论的答案：它是一种没有词典的语言。",
    author: "周砚舟",
    date: "2026-09-12",
    category: "theory",
    tags: ["符号学", "麦茨", "影像语言"],
    cover:
      "https://readdy.ai/api/search-image?query=Minimal%20still%20life%20of%20scattered%20typewriter%20keys%20and%20small%20photographic%20frames%20on%20a%20warm%20paper%20surface%2C%20blue-gray%20accents%2C%20soft%20window%20light%2C%20film%20grain%2C%20editorial%20design%20aesthetic%2C%20calm%20and%20structured%20mood&width=800&height=500&seq=tf-theory-03&orientation=landscape",
    views: 11870,
    favorites: 720,
    featured: false,
    content: [
      {
        type: "p",
        text: "语言拥有词汇与语法，而电影没有固定的「词」。麦茨意识到，正是这一差异，让电影成为一种独特而迷人的表意系统。",
      },
      { type: "h2", text: "大组合段：一种实用的分析工具" },
      {
        type: "p",
        text: "麦茨提出了「大组合段」的概念，试图把一场戏中连续的镜头段落分类归纳。这套工具帮助批评者从直觉式的感受走向可分析的结构。",
      },
      {
        type: "quote",
        text: "电影是一种没有词典的语言，它的语法藏在每一部具体的影片之中。",
      },
      { type: "h2", text: "它的价值与边界" },
      {
        type: "p",
        text: "符号学使影评获得了方法论上的自觉，但它也提醒我们：影像的意义从不完全封闭，观众的语境同样参与其中。",
      },
    ],
  },
  {
    id: "auteur-theory",
    title: "作者论的兴衰：从《电影手册》到当代批评",
    summary:
      "导演是否是一部电影的真正「作者」？作者论曾重塑影评的地图，也留下了诸多值得再问一遍的难题。",
    author: "沈砚",
    date: "2026-09-03",
    category: "theory",
    tags: ["作者论", "电影手册", "导演研究"],
    cover:
      "https://readdy.ai/api/search-image?query=Director%20chair%20and%20megaphone%20in%20an%20empty%20studio%20with%20soft%20haze%2C%20muted%20blue-gray%20and%20warm%20beige%20tones%2C%20editorial%20photography%2C%20film%20grain%2C%20cinematic%20atmosphere%2C%20balanced%20composition&width=800&height=500&seq=tf-theory-04&orientation=landscape",
    views: 10420,
    favorites: 610,
    featured: false,
    content: [
      {
        type: "p",
        text: "上世纪五十年代，一批法国影评人提出：真正决定一部影片的，是导演持续的个人视野。作者论由此诞生，并迅速越过大西洋，重塑了对好莱坞的重新评价。",
      },
      { type: "h2", text: "批评方法的革命" },
      {
        type: "p",
        text: "作者论的意义在于，它把批评的重心从题材转向形式，从故事转向风格。观众开始留意反复出现的母题、场面调度的习惯与镜头背后的个性。",
      },
      {
        type: "quote",
        text: "作者论不是给导演加冕，而是为批评寻找一个可对话的对象。",
      },
      { type: "h2", text: "今天的再思考" },
      {
        type: "p",
        text: "在制片工业高度协作的今天，把一部电影归功于单一作者显得过于简化。但作者论依然提供了一种看待风格与岁月的目光。",
      },
    ],
  },
  {
    id: "gaze-mulvey",
    title: "凝视理论：劳拉·穆尔维与女性主义电影批评",
    summary:
      "镜头如何观看，又如何被观看？穆尔维的「男性凝视」概念，为电影研究打开了一个至今仍在延展的问题域。",
    author: "苏晚",
    date: "2026-08-26",
    category: "theory",
    tags: ["凝视", "女性主义", "穆尔维"],
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20minimal%20scene%20of%20curtains%20parting%20to%20reveal%20soft%20light%2C%20muted%20blue-gray%20and%20warm%20beige%20palette%2C%20film%20grain%2C%20editorial%20art%20photography%2C%20melancholic%20and%20elegant%20atmosphere%2C%20shallow%20depth%20of%20field&width=800&height=500&seq=tf-theory-05&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Wide%20artistic%20scene%20of%20heavy%20curtains%20slowly%20parting%20to%20reveal%20a%20soft%20glowing%20void%2C%20muted%20blue-gray%20and%20warm%20beige%20palette%2C%20film%20grain%2C%20editorial%20cinematic%20aesthetic%2C%20melancholic%20mysterious%20mood%2C%20spacious%20composition&width=1920&height=1080&seq=tf-hero-03&orientation=landscape",
    views: 13960,
    favorites: 1040,
    featured: true,
    content: [
      {
        type: "p",
        text: "在一篇影响深远的论文中，劳拉·穆尔维指出，主流电影往往把观看的快感建构为男性对女性身体的注视。这一结构不只在题材层面，更嵌在镜头的语法里。",
      },
      { type: "h2", text: "看与被看的不对称" },
      {
        type: "p",
        text: "问题不在于「有没有女性角色」，而在于镜头如何分配观看的权力。是谁在主动地看，又是谁被呈现为可看之物。",
      },
      {
        type: "quote",
        text: "分析的目的是拆解观看的结构，从而为别样的影像寻找可能。",
      },
      { type: "h2", text: "延续与拓展" },
      {
        type: "p",
        text: "此后，凝视理论不断被扩展至种族、阶级与后殖民维度。它把电影研究从审美引向了政治。",
      },
    ],
  },
  {
    id: "nonlinear-narrative",
    title: "叙事学视域下的非线性电影结构",
    summary:
      "当时间被打乱，意义反而更加清晰。非线性叙事如何组织记忆、因果与悬念，是当代电影叙事学的重要课题。",
    author: "林叙",
    date: "2026-08-18",
    category: "theory",
    tags: ["叙事学", "非线性", "结构"],
    cover:
      "https://readdy.ai/api/search-image?query=Surreal%20collage%20of%20clock%20faces%20and%20fragmented%20photographs%20floating%20in%20soft%20light%2C%20desaturated%20blue-gray%20and%20warm%20beige%20tones%2C%20grainy%20texture%2C%20editorial%20minimal%20aesthetic%2C%20dreamlike%20mood&width=800&height=500&seq=tf-theory-06&orientation=landscape",
    views: 9870,
    favorites: 560,
    featured: false,
    content: [
      {
        type: "p",
        text: "线性叙事以因果为绳，把事件串成一条清晰的时间线。而当代电影越来越多地选择打乱这条线，让观众在拼合中主动建构理解。",
      },
      { type: "h2", text: "时间的重新编排" },
      {
        type: "p",
        text: "倒叙、插叙、循环与多线并置，每一种结构都在重写观众与故事的关系，也在重写悬念的定义。",
      },
      {
        type: "quote",
        text: "非线性不是混乱，而是把理解的责任交还给观众。",
      },
      { type: "h2", text: "从技巧到主题" },
      {
        type: "p",
        text: "在许多作品中，结构的复杂并不服务于炫技，而与记忆、创伤和身份等主题紧密相连。",
      },
    ],
  },
  {
    id: "sound-in-cinema",
    title: "声音的在场：电影声音理论的关键转向",
    summary:
      "我们习惯把电影当作视觉艺术，却往往忽略了声音如何塑造空间、情绪与真实感。",
    author: "周砚舟",
    date: "2026-08-10",
    category: "theory",
    tags: ["声音理论", "音景", "听觉"],
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20sound%20wave%20ripples%20in%20a%20dark%20quiet%20space%2C%20muted%20blue-gray%20and%20warm%20beige%20highlights%2C%20film%20grain%2C%20editorial%20minimal%20design%2C%20calm%20and%20mysterious%20atmosphere%2C%20soft%20gradient%20light&width=800&height=500&seq=tf-theory-07&orientation=landscape",
    views: 8420,
    favorites: 470,
    featured: false,
    content: [
      {
        type: "p",
        text: "声音不是画面的附庸。它扩展了画外空间，制造了心理深度，也在无形中支配着观众的情绪节奏。",
      },
      { type: "h2", text: "画外之声" },
      {
        type: "p",
        text: "声音让银幕之外的世界获得存在感。一个脚步、一阵风声，就足以让观众相信画面之外仍有生活。",
      },
      {
        type: "quote",
        text: "听见看不见的东西，才是电影声音真正的魔力。",
      },
      { type: "h2", text: "聆听的转向" },
      {
        type: "p",
        text: "近年来，声音研究逐渐走出视觉的阴影，成为理解电影体验不可或缺的一环。",
      },
    ],
  },
  {
    id: "digital-realism",
    title: "数字时代的现实主义：虚拟影像与纪录片伦理",
    summary:
      "当影像可以随意生成，「真实」意味着什么？数字技术正迫使纪录片重新回答关于信任的老问题。",
    author: "苏晚",
    date: "2026-08-02",
    category: "theory",
    tags: ["纪录片", "数字技术", "伦理"],
    cover:
      "https://readdy.ai/api/search-image?query=Half%20real%20half%20digital%20glitched%20landscape%20horizon%2C%20desaturated%20blue-gray%20with%20warm%20beige%20light%2C%20film%20grain%2C%20editorial%20minimal%20aesthetic%2C%20uncanny%20and%20quiet%20mood%2C%20soft%20gradient%20sky&width=800&height=500&seq=tf-theory-08&orientation=landscape",
    views: 7630,
    favorites: 410,
    featured: false,
    content: [
      {
        type: "p",
        text: "影像曾经是「见证」的代名词，而如今它同样可以是「生成」的产物。这一转变深刻动摇了纪录片的真实性基础。",
      },
      { type: "h2", text: "生产真实的机器" },
      {
        type: "p",
        text: "数字工具既能复原逝去的场景，也能无中生有。创作者因此承担起新的伦理责任：如何在表达与诚实之间取得平衡。",
      },
      {
        type: "quote",
        text: "当真实可以被生成，观看者的信任就成了最珍贵的资源。",
      },
      { type: "h2", text: "新的契约" },
      {
        type: "p",
        text: "也许未来的纪录片，需要与观众重建一份关于「我们如何相信影像」的契约。",
      },
    ],
  },
  {
    id: "in-the-mood-for-love",
    title: "《花样年华》：时间褶皱里的欲望",
    summary:
      "在旗袍与走廊之间，欲望从未被直接说出。王家卫用重复与留白，拍出了一种几乎只存在于时间里的爱情。",
    author: "陈知微",
    date: "2026-09-25",
    category: "review",
    tags: ["王家卫", "华语电影", "场面调度"],
    cover:
      "https://readdy.ai/api/search-image?query=Narrow%20vintage%20corridor%20with%20warm%20lantern%20light%20and%20draped%20curtains%2C%20muted%20beige%20and%20deep%20blue-gray%20tones%2C%20film%20grain%2C%20editorial%20cinematic%20photography%2C%20nostalgic%20and%20melancholic%20atmosphere%2C%20soft%20shadow&width=800&height=500&seq=tf-review-01&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Wide%20nostalgic%20night%20scene%20of%20a%20narrow%20vintage%20corridor%20with%20warm%20lantern%20glow%20and%20draped%20curtains%2C%20deep%20blue-gray%20with%20beige%20highlights%2C%20film%20grain%2C%20editorial%20cinematic%20mood%2C%20romantic%20melancholic%20atmosphere&width=1920&height=1080&seq=tf-hero-04&orientation=landscape",
    views: 21340,
    favorites: 1980,
    featured: true,
    content: [
      {
        type: "p",
        text: "《花样年华》几乎是一部关于「没有发生」的电影。两个被背叛的人靠近，又退开，所有的情感都被压缩进走廊、楼梯与雨夜的缝隙里。",
      },
      { type: "h2", text: "重复作为修辞" },
      {
        type: "p",
        text: "王家卫反复使用相同的走位与音乐，让日常动作获得仪式感。正是在这种重复中，观众感到时间在缓慢地流逝，而情感在悄悄地加深。",
      },
      {
        type: "quote",
        text: "如果有多一张船票，你会不会跟我一起走。",
      },
      { type: "h2", text: "留白的重量" },
      {
        type: "p",
        text: "影片最动人的部分往往在画面之外。摄影机的克制，让观众不得不自己去填补那些未被说出的情感。",
      },
    ],
  },
  {
    id: "taxi-driver",
    title: "《出租车司机》：都市孤独的病理学",
    summary:
      "挡风玻璃外的霓虹，映照的是一个失序的内心。斯科塞斯拍出了城市最冷的温度。",
    author: "高野",
    date: "2026-09-16",
    category: "review",
    tags: ["斯科塞斯", "新好莱坞", "城市"],
    cover:
      "https://readdy.ai/api/search-image?query=Rain-soaked%20night%20city%20street%20seen%20through%20a%20car%20windshield%20with%20blurred%20neon%20reflections%2C%20deep%20blue-gray%20and%20warm%20amber%20tones%2C%20film%20grain%2C%20cinematic%20editorial%20mood%2C%20lonely%20atmosphere&width=800&height=500&seq=tf-review-02&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Wide%20rainy%20night%20city%20street%20with%20blurred%20neon%20reflections%20seen%20from%20inside%20a%20car%2C%20deep%20blue-gray%20and%20warm%20amber%20tones%2C%20film%20grain%2C%20cinematic%20editorial%20photography%2C%20lonely%20urban%20atmosphere%2C%20spacious%20framing&width=1920&height=1080&seq=tf-hero-05&orientation=landscape",
    views: 16980,
    favorites: 1320,
    featured: true,
    content: [
      {
        type: "p",
        text: "出租车是移动的观察室。主角透过玻璃看城市，城市却透过霓虹看着他的孤独。",
      },
      { type: "h2", text: "视点与疏离" },
      {
        type: "p",
        text: "影片大量使用主观视点与镜像，让观众既贴近主人公，又感到无法真正进入他的世界。疏离因此成为形式本身。",
      },
      {
        type: "quote",
        text: "你在跟我说话吗？",
      },
      { type: "h2", text: "一座城市的症状" },
      {
        type: "p",
        text: "与其说这是一部关于个人的电影，不如说它记录了一座城市在特定年代的情绪与病症。",
      },
    ],
  },
  {
    id: "yi-yi",
    title: "《一一》：日常的诗学与城市的孤独",
    summary:
      "杨德昌把镜头对准最平常的生活，却在平静之下，让我们看见每个人的一生。",
    author: "陈知微",
    date: "2026-09-06",
    category: "review",
    tags: ["杨德昌", "台湾电影", "家庭"],
    cover:
      "https://readdy.ai/api/search-image?query=Quiet%20modern%20Taipei%20balcony%20at%20dusk%20with%20soft%20city%20lights%2C%20muted%20blue-gray%20and%20warm%20beige%20palette%2C%20film%20grain%2C%20editorial%20photography%2C%20reflective%20and%20calm%20atmosphere&width=800&height=500&seq=tf-review-03&orientation=landscape",
    views: 14560,
    favorites: 1180,
    featured: false,
    content: [
      {
        type: "p",
        text: "《一一》的伟大在于它的平常。婚礼、葬礼、初恋、失意，这些日常片段被杨德昌编织成一首关于时间的诗。",
      },
      { type: "h2", text: "窗与镜" },
      {
        type: "p",
        text: "影片大量透过玻璃与窗户取景，人物被城市包裹。这种取景方式，让私人情感与都市空间彼此映照。",
      },
      {
        type: "quote",
        text: "婆婆，我觉得我好像白活了。",
      },
      { type: "h2", text: "看不见的另一面" },
      {
        type: "p",
        text: "小男孩拍下每个人的后脑勺，因为「他们自己看不到」。这正是全片的隐喻：我们总是看不见生活的另一面。",
      },
    ],
  },
  {
    id: "blade-runner-2049",
    title: "《银翼杀手2049》：后人类语境下的记忆与身份",
    summary:
      "当记忆可以被制造，谁还拥有真实的过去？影片以冷峻的影像，追问人之为人的边界。",
    author: "高野",
    date: "2026-08-28",
    category: "review",
    tags: ["科幻", "后人类", "维伦纽瓦"],
    cover:
      "https://readdy.ai/api/search-image?query=Neo-noir%20futuristic%20city%20in%20dense%20amber%20smog%2C%20blue-gray%20figures%20and%20warm%20gold%20light%2C%20film%20grain%2C%20cinematic%20editorial%20aesthetic%2C%20isolated%20and%20melancholic%20mood%2C%20vast%20scale&width=800&height=500&seq=tf-review-04&orientation=landscape",
    views: 15720,
    favorites: 1270,
    featured: false,
    content: [
      {
        type: "p",
        text: "影片延续了原作对「何以为人」的追问，却把焦点从复制人转向了记忆的可靠性。",
      },
      { type: "h2", text: "记忆的所有权" },
      {
        type: "p",
        text: "当记忆被写入与出售，身份便不再稳固。主角对自身过去的追寻，本质上是对存在意义的追寻。",
      },
      {
        type: "quote",
        text: "为正确的事而死，是通往真正做人的方式。",
      },
      { type: "h2", text: "影像的尺度" },
      {
        type: "p",
        text: "巨大的空间与渺小的人影形成压迫性的对比，形式的冷峻呼应了主题的孤独。",
      },
    ],
  },
  {
    id: "konyvtar-turin-horse",
    title: "《都灵之马》：尼采之马与极简影像的哲学",
    summary:
      "六个日夜，一场风，一棵树。贝拉·塔尔用极端的重复，拍出关于重复本身的哲学。",
    author: "林叙",
    date: "2026-08-20",
    category: "review",
    tags: ["贝拉·塔尔", "极简主义", "长镜头"],
    cover:
      "https://readdy.ai/api/search-image?query=Barren%20windy%20landscape%20with%20a%20lone%20bare%20tree%20and%20distant%20figure%2C%20desaturated%20blue-gray%20and%20pale%20beige%20tones%2C%20film%20grain%2C%20editorial%20cinematic%20aesthetic%2C%20bleak%20and%20meditative%20mood&width=800&height=500&seq=tf-review-05&orientation=landscape",
    views: 9120,
    favorites: 690,
    featured: false,
    content: [
      {
        type: "p",
        text: "贝拉·塔尔的最后一部影片，把镜头固定在一个几乎不再变化的世界里。重复本身成为主题，也成为修辞。",
      },
      { type: "h2", text: "重复的重量" },
      {
        type: "p",
        text: "打水、穿衣、望窗，日子以几乎相同的方式流逝。观众在重复中体会到时间的重量与生存的荒芜。",
      },
      {
        type: "quote",
        text: "一切都在重复，而重复本身，就是没有尽头。",
      },
      { type: "h2", text: "影像的极简" },
      {
        type: "p",
        text: "当一切都减到最少，剩下的那一点点变化，反而拥有了惊人的力量。",
      },
    ],
  },
  {
    id: "burning",
    title: "《燃烧》：阶级隐喻与不可靠叙事",
    summary:
      "一只不存在的流浪猫，一座没人去的温室。李沧东在悬疑的表层下，埋下一整套关于阶层与愤怒的隐喻。",
    author: "苏晚",
    date: "2026-08-12",
    category: "review",
    tags: ["李沧东", "韩国电影", "隐喻"],
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20twilight%20field%20at%20dusk%20with%20a%20single%20burning%20point%20of%20light%2C%20muted%20blue-gray%20and%20warm%20amber%20tones%2C%20film%20grain%2C%20editorial%20cinematic%20mood%2C%20mysterious%20and%20tense%20atmosphere&width=800&height=500&seq=tf-review-06&orientation=landscape",
    views: 13210,
    favorites: 990,
    featured: false,
    content: [
      {
        type: "p",
        text: "影片几乎没有给出确定的答案，却处处埋着线索。这种刻意的暧昧，正是它讨论社会议题的方式。",
      },
      { type: "h2", text: "两种世界" },
      {
        type: "p",
        text: "主人公与对手之间的差距，不只是财富，更是一种从容与「不用证明自己」的特权。",
      },
      {
        type: "quote",
        text: "有时我觉得对方在燃烧，像一座没人去的温室。",
      },
      { type: "h2", text: "愤怒的呈现" },
      {
        type: "p",
        text: "当不可靠叙事遇上阶级隐喻，影片的悬疑便不再关于真相，而关于愤怒如何被压抑与释放。",
      },
    ],
  },
  {
    id: "the-400-blows",
    title: "《四百击》：成长、自由与镜头的逃逸",
    summary:
      "最后那个定格的眼神，成为电影史上最著名的注视之一。特吕弗拍出了成长的疼痛与逃离的渴望。",
    author: "陈知微",
    date: "2026-08-04",
    category: "review",
    tags: ["特吕弗", "法国新浪潮", "成长"],
    cover:
      "https://readdy.ai/api/search-image?query=Overcast%20Paris%20street%20and%20grey%20sea%20horizon%20with%20a%20small%20distant%20figure%2C%20muted%20blue-gray%20with%20pale%20beige%20light%2C%20film%20grain%2C%20editorial%20cinematic%20aesthetic%2C%20bittersweet%20and%20free%20mood&width=800&height=500&seq=tf-review-07&orientation=landscape",
    views: 12480,
    favorites: 870,
    featured: false,
    content: [
      {
        type: "p",
        text: "新浪潮用手持摄影与实景拍摄，把电影带回街头，也带回一种未经修饰的真实感。",
      },
      { type: "h2", text: "奔跑与自由" },
      {
        type: "p",
        text: "主角一路奔跑，镜头一路跟随。奔跑既是逃离，也是对自由的朴素渴望。",
      },
      {
        type: "quote",
        text: "定格的那一刻，自由与迷茫同时停在了画面里。",
      },
      { type: "h2", text: "一个凝视的结尾" },
      {
        type: "p",
        text: "最后的定格打破了叙事的幻觉，把观众的注意直接拉回到观看本身。",
      },
    ],
  },
  {
    id: "long-days-night",
    title: "《地球最后的夜晚》：梦境、记忆与时间",
    summary:
      "当电影进入后半程，时间开始循环，记忆开始重组。毕赣用一场梦境，让观影变成一次液态的体验。",
    author: "周砚舟",
    date: "2026-07-28",
    category: "review",
    tags: ["毕赣", "华语电影", "梦境"],
    cover:
      "https://readdy.ai/api/search-image?query=Dreamlike%20misty%20mountain%20town%20at%20night%20with%20dim%20green%20lamp%20light%2C%20muted%20blue-gray%20and%20warm%20beige%20tones%2C%20film%20grain%2C%20editorial%20cinematic%20aesthetic%2C%20hypnotic%20and%20poetic%20mood&width=800&height=500&seq=tf-review-08&orientation=landscape",
    views: 10890,
    favorites: 760,
    featured: false,
    content: [
      {
        type: "p",
        text: "影片把现实与梦境缝合在一起，让时间不再是一条线，而是一片可以反复涉入的水域。",
      },
      { type: "h2", text: "长镜头的沉浸" },
      {
        type: "p",
        text: "那段长长的梦境镜头，让观众与人物一同漂浮。形式本身成为情感的直接表达。",
      },
      {
        type: "quote",
        text: "梦是记忆的另一种排序方式。",
      },
      { type: "h2", text: "观看作为体验" },
      {
        type: "p",
        text: "与其说要理解这个故事，不如说要经历它。影片要求的是一种沉浸式的观看。",
      },
    ],
  },
  {
    id: "china-film-industry",
    title: "中国电影产业十年：从票房狂欢到内容深耕",
    summary:
      "高速增长之后，行业开始重新思考：什么才是可持续的创作与市场关系。",
    author: "何川",
    date: "2026-09-30",
    category: "culture",
    tags: ["产业观察", "市场", "中国电影"],
    cover:
      "https://readdy.ai/api/search-image?query=Modern%20cinema%20auditorium%20rows%20softly%20lit%20with%20muted%20blue-gray%20seats%20and%20warm%20beige%20accents%2C%20film%20grain%2C%20editorial%20photography%2C%20clean%20and%20professional%20atmosphere&width=800&height=500&seq=tf-culture-01&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20a%20large%20empty%20modern%20cinema%20auditorium%20with%20softly%20lit%20rows%2C%20muted%20blue-gray%20and%20warm%20beige%20accents%2C%20film%20grain%2C%20clean%20editorial%20photography%2C%20professional%20calm%20mood%2C%20negative%20space&width=1920&height=1080&seq=tf-hero-06&orientation=landscape",
    views: 13640,
    favorites: 720,
    featured: true,
    content: [
      {
        type: "p",
        text: "过去十年，中国电影经历了从银幕数量扩张到内容竞争加剧的转变。增长的逻辑正在被重新书写。",
      },
      { type: "h2", text: "从规模到质量" },
      {
        type: "p",
        text: "当银幕红利逐渐见顶，观众对内容的要求反而更高。口碑与类型的匹配度，成为决定成败的关键变量。",
      },
      {
        type: "quote",
        text: "市场越成熟，越需要内容与观众之间更精细的对话。",
      },
      { type: "h2", text: "新的观察维度" },
      {
        type: "p",
        text: "除了票房，我们还需要关注创作生态、人才结构与观众审美的长期变化。",
      },
    ],
  },
  {
    id: "streaming-auteur",
    title: "流媒体时代作者电影的生存策略",
    summary:
      "当平台成为新的放映厅，作者导演如何在算法与自由之间寻找空间？",
    author: "何川",
    date: "2026-09-19",
    category: "culture",
    tags: ["流媒体", "作者电影", "平台"],
    cover:
      "https://readdy.ai/api/search-image?query=Minimal%20laptop%20screen%20glow%20in%20a%20dim%20room%20suggesting%20streaming%2C%20muted%20blue-gray%20and%20warm%20beige%20tones%2C%20film%20grain%2C%20editorial%20minimal%20aesthetic%2C%20quiet%20and%20modern%20mood&width=800&height=500&seq=tf-culture-02&orientation=landscape",
    views: 10230,
    favorites: 580,
    featured: false,
    content: [
      {
        type: "p",
        text: "流媒体为作者电影提供了新的资金与放映渠道，同时也带来了新的约束与不确定。",
      },
      { type: "h2", text: "机会与代价" },
      {
        type: "p",
        text: "平台带来了更大的潜在观众，但也让作品的呈现方式更受数据逻辑影响。",
      },
      {
        type: "quote",
        text: "被看见的机会变多了，被如何看见，却变得更难把握。",
      },
      { type: "h2", text: "在夹缝中创作" },
      {
        type: "p",
        text: "许多作者选择在平台项目与独立创作之间并行，以保持表达的弹性。",
      },
    ],
  },
  {
    id: "film-festival-ecosystem",
    title: "电影节生态与艺术电影的国际流通",
    summary:
      "首映、选片与奖项，构成了一套看不见的流通网络。理解电影节，才能理解艺术电影如何抵达观众。",
    author: "沈砚",
    date: "2026-09-08",
    category: "culture",
    tags: ["电影节", "艺术电影", "流通"],
    cover:
      "https://readdy.ai/api/search-image?query=Elegant%20festival%20red-carpet%20staircase%20in%20soft%20evening%20light%2C%20muted%20blue-gray%20with%20warm%20beige%20accents%2C%20film%20grain%2C%20editorial%20cinematic%20aesthetic%2C%20glamorous%20yet%20restrained%20mood&width=800&height=500&seq=tf-culture-03&orientation=landscape",
    views: 8870,
    favorites: 520,
    featured: false,
    content: [
      {
        type: "p",
        text: "对很多艺术电影而言，电影节才是它们真正的首映与市场的起点。",
      },
      { type: "h2", text: "选片作为策展" },
      {
        type: "p",
        text: "选片人决定了哪些影片被看见，也间接塑造了一个时期的美学风向。",
      },
      {
        type: "quote",
        text: "电影节不只是颁奖，它是一整套让影片流动起来的机制。",
      },
      { type: "h2", text: "流通的另一面" },
      {
        type: "p",
        text: "流通并不均衡。理解其中的结构，才能理解艺术电影的处境。",
      },
    ],
  },
  {
    id: "hollywood-global",
    title: "好莱坞类型片的全球化与文化折扣",
    summary:
      "同一种类型，在不同的文化语境中被重新解读。全球化并非同质化，而是一场持续的翻译。",
    author: "高野",
    date: "2026-08-30",
    category: "culture",
    tags: ["好莱坞", "全球化", "类型片"],
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20globe%20of%20thin%20light%20lines%20over%20a%20warm%20beige%20desk%2C%20muted%20blue-gray%20accents%2C%20film%20grain%2C%20editorial%20minimal%20aesthetic%2C%20calm%20analytical%20mood&width=800&height=500&seq=tf-culture-04&orientation=landscape",
    views: 7460,
    favorites: 420,
    featured: false,
    content: [
      {
        type: "p",
        text: "好莱坞类型片凭借成熟的形式走向世界，但它的意义在每个地方都会被重新书写。",
      },
      { type: "h2", text: "形式与语境" },
      {
        type: "p",
        text: "类型提供了稳定的期待框架，而观众会用自己的文化经验去填补其中的空白。",
      },
      {
        type: "quote",
        text: "全球化的电影，总是要在不同的文化里被重新翻译一次。",
      },
      { type: "h2", text: "折扣与再创造" },
      {
        type: "p",
        text: "所谓「文化折扣」，同时也是本地创造力介入的入口。",
      },
    ],
  },
  {
    id: "short-video-viewing",
    title: "短视频冲击下电影观看方式的变迁",
    summary:
      "当注意力被切成碎片，长片电影该如何重新争夺观众的时间与耐心？",
    author: "苏晚",
    date: "2026-08-22",
    category: "culture",
    tags: ["短视频", "观看方式", "注意力"],
    cover:
      "https://readdy.ai/api/search-image?query=Multiple%20small%20phone%20screens%20glowing%20in%20a%20dim%20room%20suggesting%20fragmented%20attention%2C%20muted%20blue-gray%20and%20warm%20beige%20tones%2C%20film%20grain%2C%20editorial%20minimal%20aesthetic%2C%20modern%20and%20slightly%20uneasy%20mood&width=800&height=500&seq=tf-culture-05&orientation=landscape",
    views: 9650,
    favorites: 540,
    featured: false,
    content: [
      {
        type: "p",
        text: "注意力经济改变了观看的节奏。习惯了即时反馈之后，缓慢铺陈的影像面临新的挑战。",
      },
      { type: "h2", text: "耐心作为一种稀缺" },
      {
        type: "p",
        text: "当碎片化成为常态，能够沉浸于长片，反而成为一种需要练习的能力。",
      },
      {
        type: "quote",
        text: "时间的长度被压缩之后，体验的深度也会改变。",
      },
      { type: "h2", text: "电影如何回应" },
      {
        type: "p",
        text: "电影并非只能被动适应。它也可以通过更强烈的前景形式，重新赢回注意力。",
      },
    ],
  },
  {
    id: "film-restoration",
    title: "电影修复与数字档案：文化遗产的再生",
    summary:
      "每一次修复，都是一次与时间的谈判。技术让老影像重现，也带来了关于「原貌」的新问题。",
    author: "何川",
    date: "2026-08-14",
    category: "culture",
    tags: ["修复", "档案", "文化遗产"],
    cover:
      "https://readdy.ai/api/search-image?query=Delicate%20hands%20of%20an%20archivist%20holding%20a%20reel%20of%20film%20in%20soft%20warm%20light%2C%20muted%20blue-gray%20and%20beige%20tones%2C%20film%20grain%2C%20editorial%20photography%2C%20tender%20and%20historical%20atmosphere&width=800&height=500&seq=tf-culture-06&orientation=landscape",
    views: 6820,
    favorites: 380,
    featured: false,
    content: [
      {
        type: "p",
        text: "胶片会褪色、会断裂、会消失。修复工作，是在与不可逆的损耗赛跑。",
      },
      { type: "h2", text: "技术作为记忆的延续" },
      {
        type: "p",
        text: "数字修复让更多观众得以重新看到旧片，也让电影史拥有了更稳定的保存形态。",
      },
      {
        type: "quote",
        text: "修复的不只是画面，更是一段可以被继续观看的时间。",
      },
      { type: "h2", text: "什么是「原貌」" },
      {
        type: "p",
        text: "当技术拥有更强的干预能力，关于「原作」与「还原」的讨论也变得更加重要。",
      },
    ],
  },
  {
    id: "asian-new-wave",
    title: "亚洲新浪潮：区域电影的地缘政治",
    summary:
      "从日本到东南亚，一波又一波的电影运动，如何以影像回应各自的时代与处境。",
    author: "林叙",
    date: "2026-08-06",
    category: "culture",
    tags: ["亚洲电影", "新浪潮", "地缘"],
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20collage%20of%20Asian%20city%20skylines%20and%20film%20strips%20in%20warm%20beige%20and%20blue-gray%2C%20film%20grain%2C%20editorial%20minimal%20aesthetic%2C%20thoughtful%20and%20open%20mood%2C%20soft%20light&width=800&height=500&seq=tf-culture-07&orientation=landscape",
    views: 7240,
    favorites: 410,
    featured: false,
    content: [
      {
        type: "p",
        text: "亚洲各地的电影运动，往往与政治转型、经济发展和文化认同紧密相连。",
      },
      { type: "h2", text: "影像与处境" },
      {
        type: "p",
        text: "不同的历史经验，塑造了不同的形式选择。理解地缘，才能理解风格。",
      },
      {
        type: "quote",
        text: "每一波新浪潮，都是一次对既有秩序的重新提问。",
      },
      { type: "h2", text: "区域的对话" },
      {
        type: "p",
        text: "把亚洲电影放在一起看，会看到一种既互相影响又彼此差异的复杂图景。",
      },
    ],
  },
  {
    id: "public-criticism",
    title: "影评的公共性：媒介变迁中的批评实践",
    summary:
      "从纸媒到社交平台，影评的形态在变，但它与公众对话的使命，始终没有消失。",
    author: "陈知微",
    date: "2026-07-30",
    category: "culture",
    tags: ["影评", "公共性", "媒介"],
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20reading%20corner%20with%20open%20books%20and%20a%20small%20notebook%20in%20soft%20window%20light%2C%20muted%20beige%20and%20blue-gray%20tones%2C%20film%20grain%2C%20editorial%20photography%2C%20calm%20intellectual%20atmosphere&width=800&height=500&seq=tf-culture-08&orientation=landscape",
    views: 6590,
    favorites: 360,
    featured: false,
    content: [
      {
        type: "p",
        text: "影评的价值，不在于给出定论，而在于为公众提供一种更好的理解方式。",
      },
      { type: "h2", text: "从版面的中心到信息流" },
      {
        type: "p",
        text: "媒介变迁改变了影评的位置与长度，却也创造了新的对话可能。",
      },
      {
        type: "quote",
        text: "好的批评，是邀请更多人一起来看、一起来想。",
      },
      { type: "h2", text: "批评的当下任务" },
      {
        type: "p",
        text: "在信息过载的环境里，认真而有温度的批评，本身就是一种稀缺的内容。",
      },
    ],
  },
];